/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { UserService } from '@shared/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, interval } from 'rxjs';
import { ModalController, Platform, ToastController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { Preferences } from '@capacitor/preferences';
import { PicturesPage } from '../pages/pictures/pictures.page';
import { Router } from '@angular/router';
import { EventService } from '@shared/services/event.service';
import { UtilService } from '@shared/utils/util';
import { getSoulOid } from '@shared/utils/soul-id.util';

@Injectable({
  providedIn: 'root'
})
export class MatchService {
  private matchSubject = new Subject<number>();
  public userData : any;
  public souls: any = [];
  private refreshSubs : Subscription = new Subscription();
  /** Cancels prior in-flight request so responses cannot apply out of order. */
  private checkNewMessagesSub?: Subscription;
  private loadMatchesSub?: Subscription;
  public chattingToSoul: string| any = null;

  public haveMatchWithMessages: boolean = false;
  public haveMatch: boolean = false;

  // Um contador POR ENDPOINT. Antes era um so, compartilhado: bastava um dos dois
  // responder OK para zerar o do outro, entao uma sessao invalida que devolvia
  // {not ok} num endpoint e 200 no outro NUNCA alcancava o limite — e o app seguia
  // exibindo dados velhos em vez de deslogar.
  private notOkGetMatches: number = 0;
  private notOkNewMessages: number = 0;
  private static readonly NOT_OK_LOGOUT_THRESHOLD = 3;

  constructor(public userService: UserService,
              private translate: TranslateService,
              private platform: Platform, 
              private toastCtrl: ToastController,
              private util: UtilService,
              private router: Router,
              public http: HttpClient,
              public modalCtrl: ModalController,
              private eventService: EventService) {


    console.log('Hello MatchServiceProvider');
    this.ensureLoggedInAndLoad();

    this.startRefresh(false);

    platform.ready().then(() => {

      // O cache de conversas e' POR USUARIO, mas MATCHES nao carrega essa informacao.
      // Sem confrontar o dono, uma troca de conta na mesma instalacao faz a chat list
      // abrir com as conversas de quem saiu — foi o que motivou MATCHES_OWNER.
      // Cache sem dono e' de uma versao anterior: descartado e rebuscado do servidor.
      Promise.all([
        Preferences.get({key:'MATCHES'}),
        Preferences.get({key:'MATCHES_OWNER'}),
        Preferences.get({key:'USER_DATA'}),
      ]).then(([cache, dono, user]) => {
        let logado: string | null = null;
        try {
          logado = user.value ? getSoulOid(JSON.parse(user.value)) : null;
        } catch { logado = null; }

        if (!cache.value || !dono.value || !logado || dono.value !== logado) {
          if (cache.value) {
            console.log('[MatchService] cache de conversas descartado: dono nao confere com o usuario logado');
          }
          this.souls = [];
          this.haveMatch = false;
          this.haveMatchWithMessages = false;
          return;
        }

        const val = JSON.parse(cache.value);
        this.souls = val;
        this.haveMatch = this.souls.length > 0;
        this.haveMatchWithMessages = this.souls.some((soul: any) => soul.lastMessage != null);
      })
    })
  }

  public restartRefresh(){
    this.stopRefresh();
    this.ensureLoggedInAndLoad();
    this.startRefresh(false);
  }

  public stopRefresh(){
    this.checkNewMessagesSub?.unsubscribe();
    this.loadMatchesSub?.unsubscribe();
    this.refreshSubs.unsubscribe();
    this.refreshSubs = interval(1000 * 60 * 30).subscribe(_x => { // subscribe the 30 minutes
      this.ensureLoggedInAndCheckForNew();
    });

  }

  public startRefresh(refreshNow: boolean){
    if(refreshNow){
      this.ensureLoggedInAndCheckForNew();
    }
    this.refreshSubs.unsubscribe();// unsubscribe the 30 minutes one
    console.log('Will subscribe for 10 seconds...');
    this.refreshSubs = interval(1000 * 10).subscribe(_x => {
      console.log('Will call ensureLoggedInAndCheckForNew...');
      this.ensureLoggedInAndCheckForNew();
    });
  }


  public getUpdates() : Subject<number> {
    return this.matchSubject;
  }
  public ensureLoggedInAndCheckForNew(){

    try {
      if(UserService.loggedOut){
        console.log('SoulServiceProvider.loggedOut. Returning within ensureLoggedInAndCheckForNew');
        return;
      }
  
      Preferences.get({key:'USER_DATA'}).then((val) => {
        val = val.value ? JSON.parse(val.value) : null;
        if(val === null){
          // No data for now
          this.souls = [];// just in case logged in with a different user
          return;
        }

        // Troca de conta SEM reiniciar o app: o servico e' singleton (providedIn root),
        // entao a validacao do construtor nao roda de novo e `souls` continuaria com as
        // conversas de quem saiu. Comparar o dono a cada adocao de userData cobre isso.
        const donoAtual = getSoulOid(val);
        const donoAnterior = getSoulOid(this.userData);
        if (donoAnterior && donoAtual && donoAnterior !== donoAtual) {
          console.log('[MatchService] usuario mudou — descartando conversas em memoria');
          this.souls = [];
          this.haveMatch = false;
          this.haveMatchWithMessages = false;
        }


        this.userData = val;

        let since: number = 0;
        if(this.souls.length > 0){
          since = this.souls[0].relevantDate;
        }

        let theObs : Observable<any>;
        if(this.userData?._id.$oid != undefined){
          theObs =  this.userService.getNewMessagesMatches(this.userData?._id.$oid, since, this.userData);
          if(theObs != null){
            this.checkNewMessagesSub?.unsubscribe();
            this.checkNewMessagesSub = theObs.subscribe(async data => {
              console.log('getNewMessagesMatches subscribe entered');
  
              if(data == '{not ok}'){
                this.notOkNewMessages++;
                console.log('getNewMessagesMatches received {not ok}. Consecutive count: ' + this.notOkNewMessages);
                if (this.notOkNewMessages < MatchService.NOT_OK_LOGOUT_THRESHOLD) {
                  return;
                }
                // Sessao recusada pelo servidor: a lista em memoria e' de uma sessao que
                // nao vale mais. Sem zerar aqui, um novo login do MESMO usuario (sem
                // reiniciar o app) passa pela checagem de dono e ressuscita a lista velha.
                this.souls = [];
                this.haveMatch = false;
                this.haveMatchWithMessages = false;
                await this.userService.clearLocalStorage();
                //this.appCtrl.getRootNav().setRoot();


                this.stopRefresh();
                this.router.navigateByUrl('/login');

                return;

              }else{
                this.notOkNewMessages = 0;

                if(data != null && data > 0){
                  this.loadMatches(true);
                  //Notify to update tab badge
                }
              }
  
            },
            err => {
              this.util.showToastUnableToConnect(this.toastCtrl);
              console.log('Error: ' + JSON.stringify(err));
            });
          }
        }


      });

        
    } catch (error) {
      console.log('Error on ensureLoggedInAndCheckForNew() on match-service:')
      console.log(error)
      console.error(error);
      
    }



  }

  public ensureLoggedInAndLoad(){

    if(!UserService.loggedOut){
      Preferences.get({ key: 'USER_DATA' }).then((val:any) => {
        val = val.value ? JSON.parse(val.value) : null;
  
        if(val === null){
          // Nothing for now
          return;
        }
        console.log('ensureLoggedInAndLoad userdata!=null');

        // Troca de conta SEM reiniciar o app: o servico e' singleton (providedIn root),
        // entao a validacao do construtor nao roda de novo e `souls` continuaria com as
        // conversas de quem saiu. Comparar o dono a cada adocao de userData cobre isso.
        const donoAtual2 = getSoulOid(val);
        const donoAnterior2 = getSoulOid(this.userData);
        if (donoAnterior2 && donoAtual2 && donoAnterior2 !== donoAtual2) {
          console.log('[MatchService] usuario mudou — descartando conversas em memoria');
          this.souls = [];
          this.haveMatch = false;
          this.haveMatchWithMessages = false;
        }


        this.userData = val;
        this.loadMatches(false);
      })
  
    }

  }

  loadMatches(_notify: boolean){
    try {
      // Check if user is loggedOut
      if(UserService.loggedOut){
        console.log('SoulServiceProvider.loggedOut. Returning within loadMatches');
        return;
      }

      // Load Matches from Server
      const theObs = this.userService.getMatches(this.userData);

      //Treat the returned data
      if(theObs != null){
        this.loadMatchesSub?.unsubscribe();
        this.loadMatchesSub = theObs.subscribe(async data => {
          console.log('getMatches subscribe entered');
          if(data != '{not ok}'){
            this.notOkGetMatches = 0;
            data = JSON.parse(data);

            this.loadScreen(data);
          }else{
            this.notOkGetMatches++;
            console.log('getMatches received {not ok}. Consecutive count: ' + this.notOkGetMatches);
            if (this.notOkGetMatches < MatchService.NOT_OK_LOGOUT_THRESHOLD) {
              return;
            }
            // Sessao recusada pelo servidor: a lista em memoria e' de uma sessao que
            // nao vale mais. Sem zerar aqui, um novo login do MESMO usuario (sem
            // reiniciar o app) passa pela checagem de dono e ressuscita a lista velha.
            this.souls = [];
            this.haveMatch = false;
            this.haveMatchWithMessages = false;
            await this.userService.clearLocalStorage();
            this.stopRefresh();
            this.router.navigateByUrl('/login');
            return;
          }

        },
        err => {
          this.loadScreen(this.souls);
          console.log('Error: ' + JSON.stringify(err));
        })
      }else{
        console.log('Matches are empty');
        // matches are empty. Show something with a sad face
      }
    } catch (error) {
      console.log('Error on loadMatches() on match-service:')
      console.log(error)
      console.error(error);

    }

  }

  loadScreen(data: any){
    try {
      if(data != null){
          
        const newSouls = [];
        let newOnes: number = 0;
  
        for (let i = data.length-1; i > -1; i--) {
          const aMatch = data[i];
  
          // Setup this as default
          aMatch.firstPic = PicturesPage.noPicture;
          this.useURLforImage(aMatch);
  
          if(aMatch.lastMessage==null){
            aMatch.theMessage = this.translate.instant('sayHi') + ' :)';
            aMatch.newBadge = !aMatch.read;
  
            if(aMatch.newBadge){
              //register match event
              // TODO
              // matches.read is set to true when user goes to Chat-List tab or only when it opens the Chat
              // To discus, in case user has to open the Chat: matches.analyticsSent = true. If matches.analyticsSent, send and set analyticsSent
              // Just save a list of SoulId for matches already sent to analytics in the client database
              // this.analytics.registerEvent(AnalyticsProvider.matchEvent);
              console.log("! Analytics 'match_chat_list' event sent !");
  
              aMatch.newBadge = false;
              console.log("aMatch.newBadge status: "+ aMatch.newBadge);
  
            }
       
          } else{
            aMatch.theMessage = (aMatch.lastMessage.message != undefined && aMatch.lastMessage.message != null)?aMatch.lastMessage.message:'';
            if(!aMatch.lastMessage.sent && !aMatch.lastMessage.read){
              aMatch.newBadge = true;
            }
            const msg = aMatch.theMessage
            if(typeof msg == "object" || (aMatch.theMessage.trim().startsWith('{') && aMatch.theMessage.trim().endsWith('}'))){
              try {
                const _audioMsg = JSON.parse(aMatch.theMessage);
                aMatch.lastMsgIsAudio = true;
              } catch (error) {
                console.log("Text message starting with { and ending with }. Error Trying to parse text as JSON");
              }
            }
  
          }
  
          if(aMatch.newBadge) {
            newOnes++;
          }
          
          const d = new Date(aMatch.relevantDate * 1000);
          aMatch.relevantTime = this.userService.formatDate(d, this.userData) + ' ' + 
                                this.userService.formatTime(d);
  
          newSouls.push( aMatch );
        }
        this.souls = newSouls;
        
        // Parse theMessage if we have an audio message
        this.souls = this.souls.map((item: any) => {
          let parsed = {};
          if (item.theMessage && typeof item.theMessage === 'string') {
            try {
              parsed = JSON.parse(JSON.parse(item.theMessage)); // Duplo parse
            } catch {
              try {
                parsed = JSON.parse(item.theMessage); // Fallback para parse simples
              } catch {
                parsed = {}; // Fallback para objeto vazio
              }
            }
          }

          return { ...item, parsedMessage: parsed };
        });

        // Grava o dono junto: e' o que permite descartar o cache numa troca de conta.
        Preferences.set({key:'MATCHES_OWNER', value: getSoulOid(this.userData) ?? ''});
        Preferences.set({key:'MATCHES', value: JSON.stringify(this.souls)}).then((_val)=>{
          console.log('matches saved to DB');
        })
        this.matchSubject.next(newOnes);
  
      }
  
    } catch (error) {
      console.log('Error on loadScreen() on match-service:')
      console.log(error)
      console.error(error);
    }
  }

  

  useURLforImage(aSoul: any){
    let picnum : number;
    for(picnum=0; picnum<9; picnum++) {
      const aPic: string = "picture" + picnum;
      if(aSoul[aPic] != null) {
       //console.log('match-service.loadMatches Contains ' + aPic);
       //console.log('aSoul[aPic].$oid: ' + aSoul[aPic].$oid);
       aSoul.firstPic = this.userService.getImageURL(aSoul[aPic].$oid); //this.soulService.getBaseURL() + '/soul/image/' + aSoul[aPic].$oid;
       break;
     }
    }

  }

  /** Updates match list UI after ChatPage successfully marked read on the server. */
  applyReadLocally(soulToChat: any): void {
    if (!soulToChat) {
      return;
    }
    soulToChat.newBadge = false;
    const otherId = getSoulOid(soulToChat);
    if (otherId && this.souls?.length) {
      for (const soul of this.souls) {
        if (getSoulOid(soul) === otherId) {
          soul.newBadge = false;
          break;
        }
      }
    }
    this.eventService.publish('updateNewCount', 1);
    this.matchSubject.next(-1);
  }
}
